import React from 'react';
import { compose } from 'redux';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import { Form as FinalForm, Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import AddIcon from '@mui/icons-material/Add';
import classNames from 'classnames';
import config from '../../config';

import { propTypes } from '../../util/types';
import { findOptionsForSelectFilter } from '../../util/search';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import {
  maxLength,
  required,
  composeValidators,
  validUserIdFormat,
  requiredFieldArrayCheckbox
} from '../../util/validators';

import {
  Form,
  Button,
  FieldTextInput,
  FieldSelect,
  FieldCheckboxGroup,
  FieldCheckbox,
  InfoTipForFields,
  EditListingWizardButtons,
  Modal,
  TextEditor, FieldRadioButton,
} from '../../components';
import CountriesMultipleSelectField from '../../components/CountriesMultipleSelectField/CountriesMultipleSelectField';

import LogoPhoto from './LogoPhoto';
import EditListingDescriptionQuestionForm from './EditListingDescriptionQuestionForm';
import { getListingValuesBeforeUpdate } from '../../containers/EditListingPage/utils';

import css from './EditListingDescriptionForm.module.css';
import EditListingSubcategoryField from './EditListingSubcategoryField';
import IconRadio from '../../components/IconRadio/IconRadio';

const TITLE_MAX_LENGTH = 60;

const EditListingDescriptionFormComponent = props => (
  <FinalForm
    {...props}
    initialValues={{
      images: props.images,
      ...props.initialValues,
    }}
    mutators={{ ...arrayMutators }}
    render={formRenderProps => {
      const {
        certificateOptions,
        className,
        disabled,
        ready,
        handleSubmit,
        intl,
        invalid,
        pristine,
        touched,
        saveActionMsg,
        updated,
        updateInProgress,
        fetchErrors,
        form,
        form: {
          mutators: { push },
        },
        values,
        isClient,
        isNewListingFlow,
        currentUser,
        onLogoImageUpload,

        showCrop,
        isMobSaf,
        publicData,
        imageUploadRequested,
        onImageUploadHandler,
        onRemoveImage,
        handleSubmitFile,
        handleCloseCropPopup,
        images,
        isDraftListing,
        onHandleValidatonPopup,
        onHandlePreviewPopup,
        listing,
        onHandleCreateAndBublishListing,
        publishSuccess,
        description
      } = formRenderProps;

      const clearSubCategories = (key) => {

        const subCategoryOptions = findOptionsForSelectFilter('subcategory', config.custom.filters);
        const childSubCategories = subCategoryOptions?.filter(c => c.category === key).map(c => c.key);
        const filteredSubCategories = values?.subcategory?.filter(c => !childSubCategories?.includes(c)) || null

        form.change('subcategory', filteredSubCategories);

        values?.subcategory?.map(item => {
          form.change(`subcategory-${item}-minPrice`, undefined);
          form.change(`subcategory-${item}-maxPrice`, undefined);
        })
      }

      const clearSubCategoriesPrice = (item) => {
        form.change(`subcategory-${item}-minPrice`, undefined);
        form.change(`subcategory-${item}-maxPrice`, undefined);
      }

      const clearDisplayNameValues = () => {
        if (!!values?.useDisplayName?.[0]) {
          form.change('displayName', null);
          form.change('displaySurname', null);
        }
      }

      const clearOtherSubcategoryValues = (item) => {
        form.change(item, undefined);
      }

      const titleMessage = intl.formatMessage({ id: 'EditListingDescriptionForm.title' });
      const titlePlaceholderMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.titlePlaceholder',
      });
      const titleRequiredMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.titleRequired',
      });
      const maxLengthMessage = intl.formatMessage(
        { id: 'EditListingDescriptionForm.maxLength' },
        {
          maxLength: TITLE_MAX_LENGTH,
        }
      );

      const descriptionMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.description',
      });
      const descriptionPlaceholderMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.descriptionPlaceholder',
      });
      const maxLength60Message = maxLength(maxLengthMessage, TITLE_MAX_LENGTH);
      const descriptionRequiredMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.descriptionRequired',
      });

      const minPriceRequiredMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.minPriceRequired',
      });
      const maxPriceRequiredMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.maxPriceRequired',
      });

      const displayYourNameMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.displayYourName',
      });
      const nameMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.name',
      });
      const surnameMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.surname',
      });

      const namePlaceholderMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.namePlaceholder',
      });
      const surnamePlaceholderMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.surnamePlaceholder',
      });

      const rootUrl = process.env.REACT_APP_CANONICAL_ROOT_URL;
      const referralLinkLabelMessage = intl.formatMessage({
        id: 'SignupForm.referralLinkLabel',
      });
      const referralLinkPlaceholderMessage = intl.formatMessage(
        {
          id: 'SignupForm.referralLinkPlaceholder',
        },
        {
          rootUrl,
        }
      );

      const referralLinkValidMessage = intl.formatMessage({
        id: 'SignupForm.referralLinkValid',
      },
        {
          lineBreak: <br />,
          rootUrl
        }
      );

      const { updateListingError, createListingDraftError, showListingsError } = fetchErrors || {};
      const errorMessageUpdateListing = updateListingError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingDescriptionForm.updateFailed" />
        </p>
      ) : null;

      // This error happens only on first tab (of EditListingWizard)
      const errorMessageCreateListingDraft = createListingDraftError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingDescriptionForm.createListingDraftError" />
        </p>
      ) : null;

      const errorMessageShowListing = showListingsError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingDescriptionForm.showListingFailed" />
        </p>
      ) : null;


      const qestionsName = values?.questions?.map(item => !isNaN(+item?.question))
      const invalidQuestionsName = !!qestionsName?.length && qestionsName.filter(item => !!item)?.length
      const firstQuestionTouched = touched['questions[0].question']
      const invalidQuestionsValue =
        !pristine &&
        firstQuestionTouched &&
        !!values?.questions?.map(item => !!item?.question).filter(item => item !== true)?.length;

      const classes = classNames(css.root, className);
      const submitReady = (updated && pristine) || ready;
      const submitInProgress = updateInProgress;

      const clientSubmitDisabled = disabled || submitInProgress

      const subcategoriesOtherDescription = values?.subcategory?.filter(subcategory => subcategory.includes('Other')) || [];
      const missingDescriptions = subcategoriesOtherDescription.filter(subcategory => {

        const fieldName = `${subcategory}Description`;
        return !values[fieldName] || values[fieldName].length === 0;
      });

      const isStartup = values?.providerType?.includes('startup');

      const productTypeOptions = findOptionsForSelectFilter('productType', config.custom.filters);
      const categoryOptions = findOptionsForSelectFilter('category', config.custom.filters);
      const subCategoryOptions = findOptionsForSelectFilter('subcategory', config.custom.filters);
      const providerTypeOptions = findOptionsForSelectFilter('providerType', config.custom.filters);
      const startupAskOptions = findOptionsForSelectFilter('startupAsk', config.custom.filters);
      const fundingStatusOptions = findOptionsForSelectFilter('fundingStatus', config.custom.filters);

      const availableSubcategoryOptions = !!values?.category?.length ? subCategoryOptions.filter(s => values?.category.includes(s.category)) : []

      const selectedSubcategoryes = !!values?.subcategory?.length
      && availableSubcategoryOptions.filter(c => values?.subcategory.includes(c.key))?.map(c => c.category)

      const uniqueCategories = [
        ...new Set(
          availableSubcategoryOptions
            .filter(c => values?.subcategory?.includes(c.key))
            .map(c => c.category)
        )
      ];

      const notAllSubcategorySelected = uniqueCategories?.length !== values?.category?.length && !values?.category?.includes('investigation');

      const providerSubmitDisabled =
        disabled ||
        submitInProgress ||
        !!invalidQuestionsName ||
        !!invalidQuestionsValue ||
        (!currentUser?.profileImage?.id?.uuid && !images?.length) ||
        !values?.questions?.[0]?.question ||
        !values?.paymentMethod?.[0] ||
        (!isClient && !values?.category?.[0]) ||
        (!!values?.category?.[0] && values?.category?.[0] !== 'investigation' && !values?.subcategory?.[0]) ||
        (!!values?.category?.[0] && !!values?.subcategory?.[0] && !!missingDescriptions?.length) ||
        (!!values?.category?.[0] && !!values?.subcategory?.[0] && notAllSubcategorySelected);

      const submitDisabled = isClient ? clientSubmitDisabled : providerSubmitDisabled

      const companySizeOptions = findOptionsForSelectFilter('companySize', config.custom.filters);
      const operatingSinceOptions = findOptionsForSelectFilter('operatingSince', config.custom.filters);
      const paymentMethodOptions = findOptionsForSelectFilter(
        'paymentMethod',
        config.custom.filters
      );

      const supportedCountries = config.stripe.supportedCountries.map(c => c.code);
      const supportedCountriesOptions = supportedCountries.map(c => {
        return { label: intl.formatMessage({ id: `PayoutDetailsForm.countryNames.${c}` }), key: c };
      });

      const filteredSubcategories = subCategoryOptions
        .filter(i => values?.subcategory?.includes(i.key))

      let subcategoryOptions = filteredSubcategories.filter(i => !i.key.includes('Other'));

      if (filteredSubcategories.find(s => s.key.includes('Other'))) {
        subcategoryOptions.push({ label: 'Other', key: 'other', category: 'other' })
      }

      subcategoryOptions.unshift({ label: 'General question', key: 'general', category: 'general' })

      supportedCountriesOptions.unshift({
        label: 'All',
        key: 'all',
      });

      const newLogo = !!images?.filter(img => !!img?.imageId?.uuid)?.length && images?.filter(img => !!img?.imageId?.uuid);
      const savedLogo = !!currentUser?.profileImage?.id?.uuid && [currentUser.profileImage];

      const logoField = (
        <LogoPhoto
          key="LogoPhoto"
          showCrop={showCrop}
          isMobSaf={isMobSaf}
          form={form}
          publicData={publicData}
          images={newLogo || savedLogo || []}
          intl={intl}
          imageUploadRequested={imageUploadRequested}
          onImageUploadHandler={onImageUploadHandler}
          onRemoveImage={onRemoveImage}
          handleSubmitFile={handleSubmitFile}
          handleCloseCropPopup={handleCloseCropPopup}
          isRequired={!isClient}
        />
      );

      const titleField = (
        <FieldTextInput
          id="title"
          name="title"
          className={css.title}
          type="text"
          label={intl.formatMessage({
            id: 'EditListingDescriptionForm.companyName',
          })}
          placeholder={intl.formatMessage({
            id: 'EditListingDescriptionForm.companyName',
          })}
          maxLength={TITLE_MAX_LENGTH}
          validate={composeValidators(required(titleRequiredMessage), maxLength60Message)}
          isRequired
        />
      );

      const descriptionField = (
        // <FieldTextInput
        //   id="description"
        //   name="description"
        //   className={css.descriptionTextBox}
        //   type="textarea"
        //   label={descriptionMessage}
        //   placeholder={descriptionPlaceholderMessage}
        //   validate={composeValidators(required(descriptionRequiredMessage))}
        //   isRequired
        // />

        <Field
          id="description"
          name="description"
          label={descriptionMessage}
          placeholder={descriptionPlaceholderMessage}
          component={TextEditor}
          fieldValue={description}
          isRequired
        />
      );

      const websiteField = (
        <FieldTextInput
          id="website"
          name="website"
          className={css.infoField}
          type="text"
          label={intl.formatMessage({
            id: 'EditListingDescriptionForm.website',
          })}
          placeholder={'wwww.example.com'}
          validate={!isClient && composeValidators(required('You need to complete this field'))}
          isRequired={!isClient}
        />
      );

      const referralLink = (
        <FieldTextInput
          className={css.textField}
          type="text"
          id="referralLink"
          name="referralLink"
          label={referralLinkLabelMessage}
          placeholder={referralLinkPlaceholderMessage}
          validate={validUserIdFormat(referralLinkValidMessage)}
        />
      );

      const addressField = (
        <FieldTextInput
          id="address"
          name="address"
          className={css.infoField}
          type="text"
          label={intl.formatMessage({
            id: 'EditListingDescriptionForm.location',
          })}
          placeholder={'example 123'}
          validate={composeValidators(required('You need to complete this field'))}
          isRequired
        />
      );

      const phoneNumberField = (
        <FieldTextInput
          id="phoneNumber"
          name="phoneNumber"
          className={css.infoField}
          type="text"
          label={intl.formatMessage({
            id: 'EditListingDescriptionForm.phoneNumber',
          })}
          placeholder={'020 xxxx xxxx'}
          validate={composeValidators(required('You need to complete this field'))}
          isRequired
        />
      );

      const whatAreYouLookingForField = (
        <FieldTextInput
          id="whatAreYouLookingFor"
          name="whatAreYouLookingFor"
          className={css.infoField}
          type="text"
          label={intl.formatMessage({
            id: 'EditListingDescriptionForm.whatServiceLookingFor',
          })}
          placeholder={'Type here...'}
          validate={composeValidators(required('You need to complete this field'))}
          isRequired
        />
      );

      const companySizeField = (
        <FieldSelect
          className={css.selectField}
          name={'companySize'}
          id={'companySize'}
          label={intl.formatMessage({
            id: 'EditListingDescriptionForm.companySize',
          })}
          validate={composeValidators(required('You need to complete this field'))}
          isRequired
        >
          <option key={''} value={''} default hidden>
            {'Select an option'}
          </option>
          {companySizeOptions.map(c => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </FieldSelect>
      );

      const operatingSinceField = (
        <FieldSelect
          className={css.selectField}
          name={'operatingSince'}
          id={'operatingSince'}
          label={intl.formatMessage({
            id: 'EditListingDescriptionForm.numberOfYears',
          })}
          tipId='EditListingDescriptionForm.operatingSinceTip'
          validate={composeValidators(required('You need to complete this field'))}
          isRequired
        >
          <option key={''} value={''} default hidden>
            {'Select an option'}
          </option>
          {operatingSinceOptions.map(c => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </FieldSelect>
      );

      const useDisplayNameField = (
        <div className={css.useDisplayName}>
          <label htmlFor="">
            <FormattedMessage id="EditListingDescriptionForm.displayYourName" />
            <InfoTipForFields tipId="EditListingDescriptionForm.displayYourNameTip" />
          </label>
          <FieldCheckbox
            id="useDisplayName"
            name="useDisplayName"
            label=" "
            value="useDisplayName"
            radio={false}
            isToogle
          />
        </div>
      );
      const displayNameField = (
        <FieldTextInput
          id="displayName"
          name="displayName"
          className={css.title}
          type="text"
          label={nameMessage}
          placeholder={namePlaceholderMessage}
          maxLength={TITLE_MAX_LENGTH}
          validate={composeValidators(required(titleRequiredMessage), maxLength60Message)}
          isRequired
        />
      );

      const displaySurnameField = (
        <FieldTextInput
          id="displaySurname"
          name="displaySurname"
          className={css.title}
          type="text"
          label={surnameMessage}
          placeholder={surnamePlaceholderMessage}
          maxLength={TITLE_MAX_LENGTH}
          validate={composeValidators(required(titleRequiredMessage), maxLength60Message)}
          isRequired
        />
      );

      const shortDescriptionField = (
        <FieldTextInput
          id="shortDescription"
          name="shortDescription"
          className={css.infoField}
          type="text"
          label={intl.formatMessage({
            id: 'EditListingDescriptionForm.businessTagLine',
          })}
          tipId="EditListingDescriptionForm.shortDescriptionTip"
          placeholder={'Type here...'}
          maxLength={50}
          validate={
            composeValidators(
              required('You need to complete this field'),
              // maxLength(intl.formatMessage(
              //   { id: 'EditListingDescriptionForm.maxLength' },
              //   { maxLength: '50' }
              // ), 50)
            )
          }
          isRequired
        />
      );

      const productTypeField = (
        <FieldCheckboxGroup
          label={intl.formatMessage({
            id: 'EditListingDescriptionForm.whichSectorsDoYouServe',
          })}
          className={css.checkboxesField}
          id={'productType'}
          name={'productType'}
          options={productTypeOptions}
          twoColumns={true}
          validate={composeValidators(requiredFieldArrayCheckbox('You need to complete this field'))}
          isRequired
        />
      );
      const categoryField = (
        <FieldCheckboxGroup
          label={intl.formatMessage({
            id: 'EditListingDescriptionForm.category',
          })}
          // tipId="EditListingDescriptionForm.categoryTip"
          className={css.checkboxesField}
          id={'category'}
          name={'category'}
          options={categoryOptions}
          twoColumns={true}
          radio={false}
          clearValues={clearSubCategories}
          selectedCategory={values?.category}
          validate={composeValidators(requiredFieldArrayCheckbox('You need to complete this field'))}
          isRequired
        />
      );

      const providerTypeField = (
        <div className={css.checkboxesField}>
          <label className={css.isRequired}>
            {intl.formatMessage({
              id: 'EditListingDescriptionForm.providerType',
            })}
          </label>

          {!isClient && !values?.providerType ? (
            <>
              <FieldRadioButton
                id="providerTypeEstablished"
                name="providerType"
                label="Established Company"
                value="establishedCompany"
              />
              <FieldRadioButton
                id="providerTypeStartup"
                name="providerType"
                label="Startup"
                value="startup"
                tipId="EditListingDescriptionForm.providerTypeTip"
              />
            </>
          ) : (
            <>
              <div className={classNames(css.customRadioButton, {[css.customRadioButtonChecked]: !isStartup})}>
                <IconRadio isChecked={!isStartup} />
                <span>Established Company</span>
              </div>
              <div className={classNames(css.customRadioButton, {[css.customRadioButtonChecked]: isStartup})}>
                <IconRadio isChecked={isStartup} />
                <span>Startup</span>
                <InfoTipForFields tipId={'EditListingDescriptionForm.providerTypeTip'} />
              </div>
            </>
          )}
        </div>
      );

      const startupAsk = isStartup && (
        <FieldCheckboxGroup
          label={intl.formatMessage({
            id: 'EditListingDescriptionForm.startupAsk',
          })}
          tipId="EditListingDescriptionForm.startupAskTip"
          className={css.checkboxesField}
          id={'startupAsk'}
          name={'startupAsk'}
          options={startupAskOptions}
          twoColumns={false}
          radio={false}
          selectedCategory={values?.startupAsk}
        />
      );

      const fundingStatus = isStartup && (
        <FieldCheckboxGroup
          label={intl.formatMessage({
            id: 'EditListingDescriptionForm.fundingStatus',
          })}
          className={css.checkboxesField}
          id={'fundingStatus'}
          name={'fundingStatus'}
          options={fundingStatusOptions}
          twoColumns={false}
          radio={true}
          selectedCategory={values?.startupAsk}
        />
      );

      const subcategoryField = (
        <EditListingSubcategoryField
          values={values}
          subCategoryOptions={subCategoryOptions}
          categoryOptions={categoryOptions}
          clearOtherSubcategoryValues={clearOtherSubcategoryValues}
        />
      );

      const geographicalFocusField = (
        <CountriesMultipleSelectField
          name="geographicalFocus"
          id="geographicalFocus"
          label={intl.formatMessage({
            id: 'EditListingDescriptionForm.geographicalFocus',
          })}
          tipId="EditListingDescriptionForm.geographicalFocusTip"
          options={supportedCountriesOptions}
          placeholder="Select country"
          isRequired
        />
      );

      const questionsField = (
        <div className={css.questionsSection}>
          <label className={css.dynamicItemLabel}>
            <span className={css.isRequired}>
              {intl.formatMessage({
                id: 'EditListingDescriptionForm.clientQuestionsTip',
              })}
            </span>
            <InfoTipForFields tipId="EditListingDescriptionForm.rfqTip" />
            <AddIcon className={css.addButton} onClick={() => push(`questions`, undefined)} />
          </label>
          <FieldArray name={`questions`}>
            {({ fields }) => {
              return fields.map((name, index) => (
                <EditListingDescriptionQuestionForm
                  index={index}
                  name={name}
                  key={index}
                  subcategory={subcategoryOptions}
                  fields={fields}
                />
              ));
            }}
          </FieldArray>
          {(!!invalidQuestionsValue || !!invalidQuestionsName) && <p className={css.error}>Enter valid question</p>}
        </div>
      );

      const paymentMethodField = (
        <>
          <h2>{intl.formatMessage({ id: 'EditListingDescriptionForm.paymentInformation' })}</h2>
          <FieldCheckboxGroup
            label={intl.formatMessage({
              id: 'EditListingDescriptionForm.paymentInformationTip',
            })}
            tipId="EditListingDescriptionForm.paymentMethodTip"
            className={css.checkboxesField}
            id={'paymentMethod'}
            name={'paymentMethod'}
            options={paymentMethodOptions}
            twoColumns={true}
            isRequired
            validate={composeValidators(requiredFieldArrayCheckbox('You need to complete this field'))}
          />
        </>
      );

      const clientFields = [
        logoField,
        titleField,
        descriptionField,
        websiteField,
        referralLink,
        // addressField,
        // phoneNumberField,
        // whatAreYouLookingForField,
        // companySizeField,
        // operatingSinceField,
        // questionsField,
        // paymentMethodField,
      ];

      const providerFields = [
        logoField,
        titleField,
        useDisplayNameField,
        displayNameField,
        displaySurnameField,
        providerTypeField,
        shortDescriptionField,
        descriptionField,
        websiteField,
        startupAsk,
        fundingStatus,
        addressField,
        phoneNumberField,
        productTypeField,
        categoryField,
        subcategoryField,
        geographicalFocusField,
        companySizeField,
        operatingSinceField,
        questionsField,
        paymentMethodField,
        referralLink,
      ];

      let formFields = isClient ? clientFields : providerFields;

      const invalidValues = getListingValuesBeforeUpdate(currentUser, images, values, isClient);

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          {errorMessageCreateListingDraft}
          {errorMessageUpdateListing}
          {errorMessageShowListing}

          {!values?.useDisplayName?.[0] ?
            formFields.filter(item => item?.props?.id !== "displayName" && item?.props?.id !== "displaySurname")
            : formFields}

          <EditListingWizardButtons
            inProgress={submitInProgress}
            disabled={submitDisabled}
            ready={submitReady}
            saveActionMsg={saveActionMsg}
            isClient={isClient}
            isNewListingFlow={isNewListingFlow}
            isDescriptionTab
            isDraftListing={isDraftListing}
            onHandlePreviewPopup={onHandlePreviewPopup}
            listingData={{ values, images, isDescriptionTab: true }}
            listing={listing}
            values={values}
            imagesArray={images}
            invalidValues={invalidValues}
            onHandleCreateAndBublishListing={onHandleCreateAndBublishListing}
            onHandleValidatonPopup={onHandleValidatonPopup}
            publishSuccess={publishSuccess}

          />
        </Form>
      );
    }}
  />
);

EditListingDescriptionFormComponent.defaultProps = { className: null, fetchErrors: null };

EditListingDescriptionFormComponent.propTypes = {
  className: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    createListingDraftError: propTypes.error,
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
  certificateOptions: arrayOf(
    shape({
      key: string.isRequired,
      label: string.isRequired,
    })
  ),
};

export default compose(injectIntl)(EditListingDescriptionFormComponent);
